// UI-4: THE sidebar. One component, rendered three ways so the phone and the desktop can never
// list different things again (they had drifted three times):
//   variant="desk"    the desktop sidebar, 272px or the remembered 72px rail
//   variant="rail"    the tablet's 72px rail (its expand button opens the drawer)
//   variant="drawer"  inside the phone (or tablet) drawer, with the build marker in its footer
// Every item comes from nav/views.js (sidebarGroups, sidebarFooter): the groups of brief 4 on
// Business and decisions 11, 56 and 57 on Personal, each gated exactly as before.
import React, { useState } from 'react';
import { v } from '../tokens';
import { itemId } from '../../nav/views';
import aideyLogo from '../../assets/sidebar-logo.svg';
import './shell.css';

function NavItem({ item, color, label, active, collapsed, onSelect, badge, badgeKind, pro }) {
  return (
    <button type="button" className="ui-nav__item" aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? label : undefined} title={label}
      style={active ? { '--ui-bg': v(color) } : undefined} onClick={() => onSelect(item)}>
      <span className="ui-nav__icon" aria-hidden="true">{item.icon}{collapsed && badge ? <span className="ui-nav__pip" /> : null}</span>
      <span className="ui-nav__label">{label}</span>
      {badge ? <span className={`ui-nav__badge ui-nav__badge--${badgeKind}`}>{badge}</span> : null}
      {pro ? <span className="ui-nav__pro">PRO</span> : null}
    </button>
  );
}

export default function Sidebar({
  variant = 'desk', collapsed = false, groups, footer, activeId, t,
  onSelect, onAsk, onAddTask, onSearch, onLogout, onToggle, onClose,
  bell, workspace, emailUnreadCount = 0, tasksDueCount = 0, hasProPlanAccess = true, hasTeamPlanAccess = true, buildSha,
  folded = {}, onFold, user,
}) {
  const rail = variant === 'rail' || (variant === 'desk' && collapsed);
  const label = (item) => (item.labelKey ? t(item.labelKey) : item.label);
  const counts = { emails: emailUnreadCount, tasks: tasksDueCount };
  const badgeFor = (item) => (item.badge && counts[item.badge] > 0 ? counts[item.badge] : null);
  // UI-4b, addendum 3: in the phone drawer the group holding the current page opens and the rest
  // start folded, for this opening only. On desktop the folds are remembered (App, per workspace).
  const [drawerFolds, setDrawerFolds] = useState(() => (variant === 'drawer'
    ? Object.fromEntries(groups.map((g) => [g.key, !g.items.some((i) => itemId(i) === activeId)]))
    : null));
  const isFolded = (key) => !rail && Boolean(drawerFolds ? drawerFolds[key] : folded[key]);
  const toggleFold = (key) => (drawerFolds
    ? setDrawerFolds((f) => ({ ...f, [key]: !f[key] }))
    : onFold && onFold(key));
  const proFor = (item) => (item.pro === 'pro' && !hasProPlanAccess) || (item.pro === 'team' && !hasTeamPlanAccess);

  return (
    <nav aria-label="Main" className={`ui-sidebar ui-sidebar--${variant === 'drawer' ? 'drawer' : 'desk'}${rail ? ' ui-sidebar--rail' : ''}`}
      data-testid={`sidebar-${variant}`}>
      <div className="ui-sidebar__head">
        <div className="ui-sidebar__top">
          <img src={aideyLogo} alt="Aidey" className="ui-sidebar__logo" />
          {variant === 'drawer' ? (
            <button type="button" className="ui-sidebar__toggle ui-hit" aria-label="Close menu" onClick={onClose}>✕</button>
          ) : (
            <button type="button" className="ui-sidebar__toggle ui-hit" onClick={onToggle}
              aria-label={rail ? 'Expand sidebar' : 'Collapse sidebar'} title={rail ? 'Expand sidebar' : 'Collapse sidebar'}>
              <span aria-hidden="true">{rail ? '»' : '«'}</span>
            </button>
          )}
        </div>
        {!rail && workspace}
        <div className="ui-sidebar__tools">
          <button type="button" className="ui-sidebar__search ui-hit" onClick={onSearch} aria-label={t('nav.search')} title={`${t('nav.search')} (⌘K)`}>
            <span aria-hidden="true">🔍</span>
            {!rail && <><span>{t('nav.search')}</span><kbd>⌘K</kbd></>}
          </button>
          {bell}
        </div>
      </div>

      <div className="ui-sidebar__nav">
        {/* Decision 74: text only, no robot. Fresh-eyes item 26 (Carla, 2026-10-12, after decision 86):
            it says "Aidey" in every variant, expanded, rail and drawer (the expanded one said "Ask Aidey"). */}
        <button type="button" className="ui-sidebar__ask" aria-current={activeId === 'AideyChat' ? 'page' : undefined}
          onClick={onAsk} title={t('nav.aidey_chat')} data-nav="AideyChat">
          {rail
            ? <span className="ui-sidebar__askshort">{t('nav.aidey_chat')}</span>
            : <span className="ui-nav__label">{t('nav.aidey_chat')}</span>}
        </button>
        <button type="button" className="ui-sidebar__add ui-addtask" onClick={onAddTask} title={t('nav.add_task')} aria-label={rail ? t('nav.add_task') : undefined}>
          <span className="ui-nav__icon" aria-hidden="true">➕</span>
          {!rail && <span className="ui-nav__label">{t('nav.add_task')}</span>}
        </button>

        {groups.map((g) => {
          const shut = isFolded(g.key);
          const listId = `ui-nav-${variant}-${g.key}`;
          return (
            <div key={g.key} className="ui-nav__group" role="group" aria-label={g.label} style={{ '--ui-bg': v(g.color) }}>
              {!rail && (
                <button type="button" className="ui-nav__grouplabel" aria-expanded={!shut} aria-controls={listId}
                  title={`${shut ? 'Show' : 'Hide'} ${g.label}`} onClick={() => toggleFold(g.key)}>
                  <span className="ui-nav__dot" aria-hidden="true" />
                  <span className="ui-nav__groupname">{g.label}</span>
                  {shut && <span className="ui-nav__hidden" aria-label={`${g.items.length} hidden`}>{g.items.length}</span>}
                  <span className="ui-nav__fold" aria-hidden="true">{shut ? '+' : '−'}</span>
                </button>
              )}
              <div id={listId} className="ui-nav__list" hidden={shut}>
                {g.items.map((item) => (
                  <NavItem key={itemId(item)} item={item} color={g.color} label={label(item)} collapsed={rail}
                    active={activeId === itemId(item)} onSelect={onSelect} badge={badgeFor(item)} badgeKind={item.badge} pro={proFor(item)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="ui-sidebar__foot">
        {footer.map((item) => (
          <NavItem key={itemId(item)} item={item} color="pastel-neutral" label={label(item)} collapsed={rail}
            active={activeId === itemId(item)} onSelect={onSelect} />
        ))}
        {/* Decision 60: the user card, with the business role and an Admin tag for platform admins;
            Log out lives in it. On the rail only the avatar shows (expand for the rest). */}
        <div className="ui-usercard" data-testid="user-card" title={rail && user ? user.name : undefined}>
          <span className="ui-usercard__avatar" aria-hidden="true">{user?.initials || '?'}</span>
          {!rail && (
            <>
              <span className="ui-usercard__who">
                <span className="ui-usercard__name">{user?.name || ''}</span>
                <span className="ui-usercard__role">
                  {user?.role}{user?.admin && <span className="ui-usercard__admin">Admin</span>}
                </span>
              </span>
              <button type="button" className="ui-usercard__logout" onClick={onLogout}>Log out</button>
            </>
          )}
        </div>
        {variant === 'drawer' && (
          <div className="ui-sidebar__build" data-testid="build-marker">build {buildSha}</div>
        )}
      </div>
    </nav>
  );
}
